// Baseline components on the design tokens (T-110): buttons, fields, cards, segmented
// controls, tables, empty states, the logo. Radix primitives are used where behaviour matters
// (menus, dialogs and toasts in overlay.tsx); everything else is plain HTML with the tokens.

import { t } from "../i18n";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";

type ButtonKind = "primary" | "secondary" | "ghost" | "danger";

export function Button({
  kind = "secondary",
  size = "md",
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { kind?: ButtonKind; size?: "sm" | "md" | "lg" }) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-[7px] border font-normal whitespace-nowrap transition-colors disabled:opacity-60 disabled:cursor-not-allowed";
  const kinds: Record<ButtonKind, string> = {
    primary: "bg-blue border-blue text-on-blue font-medium hover:bg-blue-hover hover:border-blue-hover active:bg-blue-active",
    secondary: "bg-ground border-rule text-ink hover:bg-panel",
    ghost: "bg-transparent border-transparent text-steel hover:text-ink hover:bg-panel",
    danger: "bg-err border-err text-on-blue font-medium hover:opacity-90",
  };
  const sizes = { sm: "px-2 py-1 text-xs", md: "px-3 py-[7px] text-sm", lg: "px-[18px] py-[10px] text-[15px]" };
  return <button className={`${base} ${kinds[kind]} ${sizes[size]} ${className}`} {...props} />;
}

export function Field({
  label,
  hint,
  id,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; id: string }) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-normal text-steel">
        {label}
      </label>
      <input
        id={id}
        aria-describedby={hint ? `${id}-hint` : undefined}
        className="min-h-10 rounded-[7px] border border-rule bg-ground px-3 py-2 text-[15px] text-ink placeholder:text-steel focus:border-blue focus:outline-none"
        {...props}
      />
      {hint && (
        <span id={`${id}-hint`} className="text-xs text-steel">
          {hint}
        </span>
      )}
    </div>
  );
}

export function Card({ title, children, className = "" }: { title?: string; children: ReactNode; className?: string }) {
  return (
    <section className={`rounded-[10px] border border-rule bg-ground px-4 py-[14px] ${className}`}>
      {title && <h3 className="mb-2 text-xs font-medium uppercase tracking-[0.06em] text-steel hyphens-auto [overflow-wrap:anywhere]">{title}</h3>}
      {children}
    </section>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div role="group" aria-label={label} className="inline-flex overflow-hidden rounded-[7px] border border-rule">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
          className="flex-1 border-r border-rule px-[10px] py-[6px] text-xs font-normal text-steel [word-break:keep-all] last:border-r-0 aria-pressed:bg-panel-2 aria-pressed:text-ink"
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function EmptyState({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-start gap-3 rounded-[10px] border border-dashed border-rule px-6 py-10">
      <h2 className="text-lg font-semibold">{title}</h2>
      <p className="m-0 max-w-[60ch] text-steel">{body}</p>
      {action}
    </div>
  );
}

export function Logo({ className = "h-6" }: { className?: string }) {
  // Light and dark lockups from the 66IDEAS kit; the visible one follows the theme.
  return (
    <span className={`inline-flex ${className}`} aria-label={t("brand.name")} role="img">
      <img src="/logo-light.svg" alt="" className="h-full w-auto dark-hide" />
      <img src="/logo-dark.svg" alt="" className="h-full w-auto light-hide" />
    </span>
  );
}

export function ErrorText({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="m-0 rounded-[7px] border border-err/40 bg-err/10 px-3 py-2 text-sm text-err">
      {children}
    </p>
  );
}

export interface Column<T> {
  key: string;
  header: string;
  cell: (row: T) => ReactNode;
  /// Right-aligned tabular figures.
  numeric?: boolean;
  className?: string;
}

/// A data table with the house style: small uppercase headers, quiet row rules, hover.
export function Table<T>({ columns, rows, rowKey, caption }: { columns: Column<T>[]; rows: T[]; rowKey: (row: T) => string | number; caption?: string }) {
  return (
    <div className="-mx-1 overflow-x-auto">
      {/* On a phone each row becomes a card of label and value pairs (table-stack in styles.css, T-484). */}
      <table className="table-stack w-full border-collapse text-[13px]">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr className="border-b border-rule text-left text-[11px] font-medium uppercase tracking-[0.06em] text-steel">
            {columns.map((c) => (
              <th key={c.key} scope="col" className={`px-2 py-2 font-medium ${c.numeric ? "text-right" : ""}`}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} className="border-b border-rule-soft last:border-b-0 hover:bg-panel">
              {columns.map((c) => (
                <td key={c.key} data-label={typeof c.header === "string" ? c.header : undefined} className={`px-2 py-1.5 align-top ${c.numeric ? "num text-right" : ""} ${c.className ?? ""}`}>
                  {c.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
